import { Link } from 'react-router-dom';

export default function DBRPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30"></div>
        <div className="absolute top-10 right-10 w-72 h-72 bg-white/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <Link to="/theory" className="text-blue-200 hover:text-white transition-colors text-sm flex items-center gap-2">
              <i className="ri-arrow-left-line"></i>
              理論一覧に戻る
            </Link>
          </div>
          
          <h1 className="text-5xl font-bold mb-6 leading-tight">
            DBR（ドラム・バッファ・ロープ）
          </h1>
          <p className="text-2xl text-blue-100 mb-8 leading-relaxed">
            薬局が楽になるのは「頑張る量」を減らしたとき
          </p>
          <div className="inline-block bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg border border-white/20">
            <p className="text-lg font-medium">流していい量は、最初から決まっている</p>
          </div>
        </div>
      </section>

      {/* Conclusion Section */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-8 rounded-2xl shadow-xl">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-flash-line text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-4">結論</h2>
              <p className="text-lg leading-relaxed">
                薬局が混乱する本当の原因は、「処理できない量まで仕事を流していること」です。DBRは、流す量を最初から制御するための実践理論です。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Theory Explanation */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
            <i className="ri-book-open-line text-white text-xl"></i>
          </div>
          理論の解説｜DBRとは何か
        </h2>
        
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            <strong className="text-blue-600">DBR（Drum–Buffer–Rope）</strong>は、エリヤフ・ゴールドラット が提唱したTOC（制約理論）を現場で動かすための実装技術です。
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 p-6 rounded-xl border-l-4 border-blue-600">
              <h3 className="font-bold text-gray-900 mb-2">TOCが</h3>
              <p className="text-gray-700">「全体は制約で決まる」という<strong>思想</strong>だとすれば、</p>
            </div>
            <div className="bg-indigo-50 p-6 rounded-xl border-l-4 border-indigo-600">
              <h3 className="font-bold text-gray-900 mb-2">DBRは</h3>
              <p className="text-gray-700">「じゃあ、どう動かす？」に答える<strong>運用ルール</strong>です。</p>
            </div>
          </div>
        </div>
      </section>

      {/* DBR 3 Elements */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
          DBRの3要素を一気に理解する
        </h2>
        
        <div className="space-y-6">
          {/* Drum */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border-l-8 border-red-500">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-music-2-line text-3xl text-red-600"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  ① ドラム（Drum）＝制約のリズム
                </h3>
                <p className="text-lg text-gray-700 mb-4">
                  ドラムとは、制約工程が処理できる<strong className="text-red-600">最大のペース</strong>です。
                </p>
              </div>
            </div>
            
            <div className="bg-red-50 p-6 rounded-xl mb-4">
              <p className="text-gray-700 mb-3">調剤薬局では、ほぼ確実にここです。</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-gray-800">
                  <i className="ri-checkbox-circle-fill text-red-600"></i>
                  最終鑑査に立てる薬剤師
                </li>
                <li className="flex items-center gap-2 text-gray-800">
                  <i className="ri-checkbox-circle-fill text-red-600"></i>
                  投薬対応できる人数・スペース
                </li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-4 rounded-lg">
              <p className="font-bold flex items-center gap-2">
                <i className="ri-arrow-right-circle-fill"></i>
                この処理能力が、薬局全体の上限になります。
              </p>
            </div>
          </div>

          {/* Buffer */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border-l-8 border-yellow-500">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-3xl text-yellow-600"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  ② バッファ（Buffer）＝余裕のクッション
                </h3>
                <p className="text-lg text-gray-700 mb-4">
                  バッファとは、制約の直前に置く<strong className="text-yellow-600">適量の仕掛品（WIP）</strong>です。
                </p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500">
                <p className="font-bold text-red-700 mb-1">多すぎる →</p>
                <p className="text-gray-700 text-sm">混乱・待ち時間・ミス増加</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                <p className="font-bold text-blue-700 mb-1">少なすぎる →</p>
                <p className="text-gray-700 text-sm">制約が空転（もったいない）</p>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-yellow-600 to-yellow-700 text-white p-4 rounded-lg">
              <p className="font-bold flex items-center gap-2">
                <i className="ri-arrow-right-circle-fill"></i>
                「ちょうどいい待ち」を意図的に作ります。
              </p>
            </div>
          </div>

          {/* Rope */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border-l-8 border-green-500">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-git-branch-line text-3xl text-green-600"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  ③ ロープ（Rope）＝投入制御
                </h3>
                <p className="text-lg text-gray-700 mb-4">
                  ロープとは、仕事を<strong className="text-green-600">入れていいタイミング</strong>を縛るルールです。
                </p>
              </div>
            </div>
            
            <div className="bg-green-50 p-6 rounded-xl mb-4">
              <p className="font-bold text-green-700 mb-3 text-lg">ここが最重要。</p>
              <ul className="space-y-2 mb-4">
                <li className="flex items-center gap-2 text-gray-700">
                  <i className="ri-close-circle-fill text-red-500"></i>
                  混んでいても受付を止めない
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <i className="ri-close-circle-fill text-red-500"></i>
                  重い処方を次々投入する
                </li>
              </ul>
              <p className="text-gray-700">これはすべて👉 <strong className="text-red-600">ロープが切れている状態</strong>です。</p>
            </div>
            
            <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4 rounded-lg">
              <p className="font-bold">ロープが、全体を守る最後の砦です。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Translation Table */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
          調剤薬局でDBRを翻訳するとこうなる
        </h2>
        
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
              <tr>
                <th className="px-6 py-4 text-left text-lg font-bold">DBR用語</th>
                <th className="px-6 py-4 text-left text-lg font-bold">薬局での意味</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr className="hover:bg-red-50 transition-colors">
                <td className="px-6 py-4 font-bold text-red-600 text-lg">ドラム</td>
                <td className="px-6 py-4 text-gray-700">鑑査・投薬の処理能力</td>
              </tr>
              <tr className="hover:bg-yellow-50 transition-colors">
                <td className="px-6 py-4 font-bold text-yellow-600 text-lg">バッファ</td>
                <td className="px-6 py-4 text-gray-700">鑑査前の適正WIP</td>
              </tr>
              <tr className="hover:bg-green-50 transition-colors">
                <td className="px-6 py-4 font-bold text-green-600 text-lg">ロープ</td>
                <td className="px-6 py-4 text-gray-700">受付制限・投入ルール</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Problem Pattern */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-8">
          現場でよくある「DBRがない薬局」
        </h2>
        
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <p className="text-lg text-gray-700 mb-6">こんな光景、ありませんか？</p>
          
          <div className="space-y-3 mb-8">
            {[
              '受付はどんどん受ける',
              '調剤は全力で作る',
              '鑑査が詰まる',
              '投薬が遅れる',
              '待合が荒れる',
              'ミスリスクが急上昇'
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3 bg-red-50 p-4 rounded-lg border-l-4 border-red-500">
                <div className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  {index + 1}
                </div>
                <p className="text-gray-800 font-medium">{item}</p>
              </div>
            ))}
          </div>
          
          <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-6 rounded-xl">
            <p className="text-xl font-bold mb-2 flex items-center gap-2">
              <i className="ri-arrow-right-circle-fill"></i>
              誰もサボっていないのに、全体が崩れる。
            </p>
            <p className="text-lg">原因は一つ。<strong className="text-yellow-300">ロープがない。</strong></p>
          </div>
        </div>
      </section>

      {/* Implementation */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-8">
          DBRを入れると、現場はこう変わる
        </h2>
        
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-red-600 text-xl">
                ①
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-3">ドラムを基準にする</h3>
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-gray-700">
                    「鑑査が1時間に◯枚処理できる」→ <strong className="text-red-600">それ以上は流さない</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-yellow-600 text-xl">
                ②
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-3">バッファを決める</h3>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-gray-700">
                    鑑査前のWIPは→ <strong className="text-yellow-600">常に◯枚を目安に維持</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-green-600 text-xl">
                ③
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-3">ロープを張る</h3>
                <div className="bg-green-50 p-6 rounded-lg">
                  <p className="text-gray-700 mb-4 font-medium">WIPが◯枚を超えたら</p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-gray-700">
                      <i className="ri-checkbox-circle-fill text-green-600"></i>
                      新規受付を一時止める
                    </li>
                    <li className="flex items-center gap-2 text-gray-700">
                      <i className="ri-checkbox-circle-fill text-green-600"></i>
                      一包化・施設処方の新規投入を止める
                    </li>
                    <li className="flex items-center gap-2 text-gray-700">
                      <i className="ri-checkbox-circle-fill text-green-600"></i>
                      調剤側は"作らない"選択をする
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6 rounded-xl text-center">
            <p className="text-2xl font-bold flex items-center justify-center gap-2">
              <i className="ri-arrow-right-circle-fill"></i>
              入れない判断が、全体を守る。
            </p>
          </div>
        </div>
      </section>

      {/* Stop is not Bad */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white rounded-2xl shadow-xl p-8">
          <h2 className="text-3xl font-bold mb-6">「止める＝悪」ではない</h2>
          
          <p className="text-lg mb-6 leading-relaxed">
            多くの薬局で、
          </p>
          
          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl mb-6">
            <ul className="space-y-2">
              <li className="flex items-center gap-2">
                <i className="ri-stop-circle-line text-xl"></i>
                受付を止める
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-time-line text-xl"></i>
                処方を待ってもらう
              </li>
            </ul>
            <p className="mt-4">ことに強い抵抗があります。</p>
          </div>
          
          <div className="bg-red-500 p-6 rounded-xl mb-6">
            <p className="text-xl font-bold mb-3">しかしDBRでは、これは正反対。</p>
            <p className="text-2xl font-bold">止めないことが、最大の事故要因</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {[
              'WIP過多',
              '投薬集中力低下',
              '聞き漏らし',
              '誤薬'
            ].map((item, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm p-4 rounded-lg flex items-center gap-3">
                <i className="ri-alert-fill text-yellow-300 text-xl"></i>
                <span className="font-medium">{item}</span>
              </div>
            ))}
          </div>
          
          <div className="bg-white text-indigo-700 p-6 rounded-xl text-center">
            <p className="text-2xl font-bold flex items-center justify-center gap-2">
              <i className="ri-shield-check-fill"></i>
              DBRは、安全管理そのものです。
            </p>
          </div>
        </div>
      </section>

      {/* KPI Section */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
            <i className="ri-bar-chart-box-line text-white text-xl"></i>
          </div>
          KPI｜DBRで見る数字はこれだけ
        </h2>
        
        <p className="text-lg text-gray-700 mb-8">複雑な指標はいりません。</p>
        
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl shadow-lg p-6 border-t-4 border-red-500">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-speed-line text-2xl text-red-600"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">制約工程の処理能力</h3>
            <p className="text-gray-600 text-sm">枚／時</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-6 border-t-4 border-yellow-500">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-stack-line text-2xl text-yellow-600"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">制約前WIP</h3>
            <p className="text-gray-600 text-sm">枚数</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-6 border-t-4 border-green-500">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-stop-circle-line text-2xl text-green-600"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">ロープ発動回数</h3>
            <p className="text-gray-600 text-sm">止めた回数</p>
          </div>
        </div>
        
        <div className="mt-8 bg-blue-50 p-6 rounded-xl border-l-4 border-blue-600">
          <p className="text-gray-700">
            この3つで、<strong className="text-blue-600">「今日はなぜ荒れたか」</strong>が説明できます。
          </p>
        </div>
      </section>

      {/* PharmaBeast Note */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="bg-gradient-to-br from-teal-500 to-teal-600 text-white rounded-2xl shadow-xl p-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-flask-line text-2xl"></i>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4">PharmaBeast的に</h3>
              <p className="text-lg leading-relaxed mb-4">
                （ピーク時）平均して鑑査・投薬を<strong className="text-yellow-300">6～12分</strong>で対応すれば、1時間に<strong className="text-yellow-300">5～10人</strong>は対応可能（1人の薬剤師）
              </p>
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg text-sm">
                <p className="mb-1">※診療科や処方内容にもよります。</p>
                <p>※薬歴は後回し</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Action Section */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="bg-gradient-to-br from-orange-500 to-red-600 text-white rounded-2xl shadow-xl p-8">
          <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
            <i className="ri-rocket-line text-4xl"></i>
            明日の一手｜DBRの第一歩
          </h2>
          
          <p className="text-lg mb-6">明日やることは、これだけ。</p>
          
          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl mb-6">
            <p className="text-2xl font-bold mb-4 flex items-center gap-2">
              <i className="ri-arrow-right-circle-fill"></i>
              「鑑査前WIPが◯枚を超えたら止める」というルールを1つ決める。
            </p>
            <p className="text-lg">紙1枚、ホワイトボード1行で十分です。</p>
          </div>
          
          <div className="bg-white text-orange-600 p-6 rounded-xl">
            <p className="text-lg font-bold mb-4">それだけで、</p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-2xl"></i>
                <span className="font-medium">待ち時間</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-2xl"></i>
                <span className="font-medium">現場ストレス</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-2xl"></i>
                <span className="font-medium">ミスリスク</span>
              </div>
            </div>
            <p className="text-lg font-bold mt-4">が、同時に下がり始めます。</p>
          </div>
        </div>
      </section>

      {/* Related Theories */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">関連する理論</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <Link to="/theory/toc" className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all hover:-translate-y-1">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-links-line text-2xl text-blue-600"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">TOC（制約理論）</h3>
            <p className="text-gray-600 text-sm">DBRの基礎となる理論</p>
          </Link>
          
          <Link to="/theory" className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all hover:-translate-y-1">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-flow-chart text-2xl text-green-600"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">リトルの法則</h3>
            <p className="text-gray-600 text-sm">WIP制御の数学的根拠</p>
          </Link>
          
          <Link to="/theory" className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all hover:-translate-y-1">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-water-flash-line text-2xl text-purple-600"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">フロー効率</h3>
            <p className="text-gray-600 text-sm">流れを最適化する視点</p>
          </Link>
        </div>
      </section>
    </div>
  );
}
